import { query } from "./strapi";

// Define types for cache objects
interface CountryData {
  id: string;
  name: string;
  thumbnail: { url: string };
}

interface DistrictData {
  id: string;
  distrito: string;
  opsLeadTime: number | null;
  ubigeoCode: string;
  deliveryFeeZone: { documentId: string; name: string } | null;
  deliveryLeadTimeZone: { documentId: string; name: string } | null;
}

// Define proper type for the cache object
interface LocationCache {
  countries: CountryData[] | null;
  departments: string[] | null;
  provinces: { [department: string]: string[] };
  districts: { [key: string]: DistrictData[] };
}

// Add cache objects for each location type with proper typing
const cache: LocationCache = {
  countries: null,
  departments: null,
  provinces: {},
  districts: {},
};

export function getCountries() {
  // Return cached data if available
  if (cache.countries) {
    console.log("Returning cached countries data");
    return Promise.resolve(cache.countries);
  }

  return query("ubigeo-countries")
    .then((res) => {
      const data = res?.data || [];
      // Store in cache
      cache.countries = data;
      return data;
    })
    .catch((err) => {
      console.error("Error fetching countries:", err);
      return []; // Return an empty array in case of error
    });
}

export function getDepartments() {
  // Return cached data if available
  if (cache.departments) {
    console.log("Returning cached departments data");
    return Promise.resolve(cache.departments);
  }

  return query("ubigeo-peru-distrito/departments")
    .then((res) => {
      const data = res?.data || [];
      // Store in cache
      cache.departments = data;
      return data;
    })
    .catch((err) => {
      console.error("Error fetching departments:", err);
      return []; // Return an empty array in case of error
    });
}

export function getProvinces(department: string) {
  // Return cached data if available for this department
  if (cache.provinces[department]) {
    console.log(`Returning cached provinces data for ${department}`);
    return Promise.resolve(cache.provinces[department]);
  }

  return query(`ubigeo-peru-distrito/provinces?department=${department}`)
    .then((res) => {
      const data = res?.data || [];
      // Store in cache by department
      cache.provinces[department] = data;
      return data;
    })
    .catch((err) => {
      console.error("Error fetching provinces:", err);
      return []; // Return an empty array in case of error
    });
}

export async function getDistricts(department: string, province: string) {
  // Create a cache key combining department and province
  const cacheKey = `${department}:${province}`;

  // Return cached data if available
  if (cache.districts[cacheKey]) {
    console.log(`Returning cached districts data for ${cacheKey}`);
    return cache.districts[cacheKey];
  }

  try {
    const response = await query(
      `ubigeo-peru-distrito/districts?department=${department}&province=${province}`
    );
    console.log("Response from getDistricts:", response);

    if (!response.data || !Array.isArray(response.data)) {
      throw new Error("Invalid response structure");
    }

    const districts = response.data.map((district: any) => {
      if (!district) {
        throw new Error("Missing attributes in district data");
      }

      return {
        id: district.id,
        distrito: district.distrito,
        opsLeadTime: district.opsLeadTime,
        ubigeoCode: district.ubigeoCode,
        // New zone arrays for array-based delivery zones (from backend enhancement)
        deliveryFeeZones: district.deliveryFeeZones || [],
        deliveryLeadTimeZones: district.deliveryLeadTimeZones || [],
        // Legacy compatibility - keep single zone fields for backward compatibility
        deliveryFeeZone: district.deliveryFeeZone
          ? {
              documentId: district.deliveryFeeZone.documentId,
              name: district.deliveryFeeZone.name,
            }
          : null,
        deliveryLeadTimeZone: district.deliveryLeadTimeZone
          ? {
              documentId: district.deliveryLeadTimeZone.documentId,
              name: district.deliveryLeadTimeZone.name,
            }
          : null,
      };
    });

    // Store in cache
    cache.districts[cacheKey] = districts;
    return districts;
  } catch (error) {
    console.error("Error fetching districts:", error);
    return [];
  }
}

// Add function to clear cache if needed (for debugging or forced refreshes)
export function clearLocationCache() {
  cache.countries = null;
  cache.departments = null;
  cache.provinces = {};
  cache.districts = {};
  console.log("Location cache cleared");
}
